import type { EngineParticipant, EngineResult, GeneratedMatch } from './types';
import { generateSeedOrder, nextPowerOfTwo } from './seed-order';

function matchId(round: number, slot: number): string {
  return `SE-R${round}-S${slot}`;
}

/**
 * Возвращает участников, отсортированных по seed (1, 2, 3, ...). Те, у кого нет
 * seed-а, ставятся в конец в исходном порядке.
 */
function sortBySeed(participants: EngineParticipant[]): EngineParticipant[] {
  const seeded = participants
    .filter((p) => p.seed !== undefined)
    .sort((a, b) => a.seed! - b.seed!);
  const unseeded = participants.filter((p) => p.seed === undefined);
  return [...seeded, ...unseeded];
}

/**
 * Раскладывает участников по слотам первого раунда. Возвращает массив длиной `bracketSize`,
 * где элемент по индексу i — id участника на слот (i+1), или null если bye.
 */
function placeParticipants(
  sorted: EngineParticipant[],
  bracketSize: number
): (string | null)[] {
  const seedOrder = generateSeedOrder(bracketSize); // [1, 8, 5, 4, ...] для bracketSize=8
  const slots: (string | null)[] = new Array(bracketSize).fill(null);

  for (let slotIdx = 0; slotIdx < bracketSize; slotIdx++) {
    const seedNumber = seedOrder[slotIdx]; // какой по силе игрок занимает этот слот
    const participantIdx = seedNumber - 1; // 0-based
    slots[slotIdx] = sorted[participantIdx]?.id ?? null;
  }
  return slots;
}

export function generateSingleElim(participants: EngineParticipant[]): EngineResult {
  if (participants.length < 2) {
    throw new Error('Нужно минимум 2 участника');
  }
  if (new Set(participants.map((p) => p.id)).size !== participants.length) {
    throw new Error('Дублирующиеся id участников');
  }

  const bracketSize = nextPowerOfTwo(participants.length);
  const rounds = Math.log2(bracketSize);
  const sorted = sortBySeed(participants);
  const slots = placeParticipants(sorted, bracketSize);

  const matches: GeneratedMatch[] = [];

  // Раунд 1: пары (slot 0,1), (slot 2,3), ...
  for (let i = 0; i < bracketSize; i += 2) {
    const slot = i / 2 + 1;
    matches.push({
      id: matchId(1, slot),
      stage: 'MAIN',
      round: 1,
      slot,
      playerAId: slots[i],
      playerBId: slots[i + 1],
    });
  }

  // Проставляем autoAdvanceWinnerId для bye-матчей
  for (const m of matches) {
    if (m.round === 1) {
      if (m.playerAId && m.playerBId === null) {
        m.autoAdvanceWinnerId = m.playerAId;
      } else if (m.playerBId && m.playerAId === null) {
        m.autoAdvanceWinnerId = m.playerBId;
      }
    }
  }

  // Раунды 2..rounds: создаём пустые матчи
  for (let r = 2; r <= rounds; r++) {
    const matchesInRound = bracketSize / 2 ** r;
    for (let s = 1; s <= matchesInRound; s++) {
      matches.push({
        id: matchId(r, s),
        stage: 'MAIN',
        round: r,
        slot: s,
        playerAId: null,
        playerBId: null,
      });
    }
  }

  // Прошиваем nextMatchId/nextMatchSlot для всех нефинальных
  for (const m of matches) {
    if (m.round === rounds) continue;
    const nextRound = m.round + 1;
    const nextSlot = Math.ceil(m.slot / 2);
    const nextId = matchId(nextRound, nextSlot);
    m.nextMatchId = nextId;
    // Победитель slot=1 → A, slot=2 → B, slot=3 → A, slot=4 → B
    m.nextMatchSlot = m.slot % 2 === 1 ? 'A' : 'B';
  }

  // Прокидываем bye-победителей в следующий раунд
  const byId = new Map(matches.map((m) => [m.id, m]));
  for (const m of matches) {
    if (!m.autoAdvanceWinnerId || !m.nextMatchId) continue;
    const next = byId.get(m.nextMatchId);
    if (!next) continue;
    if (m.nextMatchSlot === 'A') {
      next.playerAId = m.autoAdvanceWinnerId;
    } else {
      next.playerBId = m.autoAdvanceWinnerId;
    }
  }

  return { matches, rounds };
}

export function applyResult(
  matches: GeneratedMatch[],
  matchId: string,
  winnerId: string
): { updatedMatches: GeneratedMatch[] } {
  const idx = matches.findIndex((m) => m.id === matchId);
  if (idx === -1) {
    throw new Error(`Match ${matchId} not found`);
  }
  const match = matches[idx];
  if (match.autoAdvanceWinnerId) {
    throw new Error('Нельзя применять результат к bye-матчу');
  }
  if (winnerId !== match.playerAId && winnerId !== match.playerBId) {
    throw new Error(`Winner ${winnerId} is not a player in ${matchId}`);
  }

  if (!match.nextMatchId) {
    return { updatedMatches: matches };
  }

  const nextIdx = matches.findIndex((m) => m.id === match.nextMatchId);
  if (nextIdx === -1) {
    throw new Error(`Next match ${match.nextMatchId} not found`);
  }

  const updatedMatches = matches.slice();
  const nextMatch = { ...matches[nextIdx] };
  if (match.nextMatchSlot === 'A') {
    nextMatch.playerAId = winnerId;
  } else {
    nextMatch.playerBId = winnerId;
  }
  updatedMatches[nextIdx] = nextMatch;
  return { updatedMatches };
}
